/**
 * Pure helper: does the given privilege set grant access to a frontend module?
 *
 * The predicate map is exhaustive over `ModuleId`. TypeScript enforces that
 * every `ModuleId` has a predicate, so adding a new module is caught at
 * compile time if the caller forgets to register it.
 *
 * Wildcard '*' short-circuits `true` in every predicate — superadmin bypass.
 *
 * Adding a new module requires THREE coordinated edits:
 *   1. Extend `ModuleId` here + register a predicate in `PREDICATES`.
 *   2. Add the corresponding entry to `packages/ops-platform-v3/src/frontend/shell/modules.ts`.
 *   3. Add the module's privileges to `packages/shared/src/admin-privileges.ts`
 *      (ALL_ADMIN_PRIVILEGES + PRIVILEGE_DESCRIPTIONS + PRIVILEGE_RESOURCE_GROUPS)
 *      so the Admin role-edit UI can assign them — without this edit the
 *      privilege exists but no admin can grant it through the UI, only
 *      seeded superadmin (with `*`) can access the module.
 */
import { OPSV3_EXPOAMS_SCAN, OPSV3_EXPOAMS_INVENTORY_READ, OPSV3_STATION_DATA_DASHBOARD_TABLE_READ, OPSV3_STATION_DATA_DASHBOARD_ANALYSIS_READ, OPSV3_STATION_DATA_DASHBOARD_CHARTS_READ, } from './privileges-opsv3.js';
const PREDICATES = {
    expoams: (privileges) => privileges.includes('*') ||
        privileges.includes(OPSV3_EXPOAMS_SCAN) ||
        privileges.includes(OPSV3_EXPOAMS_INVENTORY_READ),
    settings: () => true,
    admin: (p) => p.includes('*') ||
        p.includes('user.read') ||
        p.includes('group.read') ||
        p.includes('grant.read') ||
        p.includes('role.read') ||
        p.includes('audit.read'),
    stationDataDashboard: (privileges) => privileges.includes('*') ||
        privileges.includes(OPSV3_STATION_DATA_DASHBOARD_TABLE_READ) ||
        privileges.includes(OPSV3_STATION_DATA_DASHBOARD_ANALYSIS_READ) ||
        privileges.includes(OPSV3_STATION_DATA_DASHBOARD_CHARTS_READ),
    bom: (p) => p.includes('*') || p.some((x) => x.startsWith('opsv3.bom.')),
    inventory: (p) => p.includes('*') || p.some((x) => x.startsWith('opsv3.inventory.')),
    commercialConsole: (p) => p.includes('*') || p.some((x) => x.startsWith('opsv3.commercial_console.')),
    // Plan email-01 — nav entries in shell/modules.ts deliberately deferred to
    // email-03 / conference-02 (no dead nav links while the routes don't exist).
    email: (p) => p.includes('*') || p.some((x) => x.startsWith('opsv3.email.')),
    conference: (p) => p.includes('*') || p.some((x) => x.startsWith('opsv3.conference.')),
    // Plan procurement-01 — substrate. Frontend mounts a placeholder shell in
    // ops-platform-v3 (the Quote → PO → Invoice surfaces land in procurement-02+).
    procurement: (p) => p.includes('*') || p.some((x) => x.startsWith('opsv3.procurement.')),
};
export function canAccess(moduleId, privileges) {
    return PREDICATES[moduleId](privileges);
}
//# sourceMappingURL=module-access.js.map